/*
 * Pipeline Theme
 *
 * Use this file to add custom Javascript to Pipeline.  Keeping your custom
 * Javascript in this fill will make it easier to update Pipeline. In order
 * to use this file you will need to open layout/theme.liquid and uncomment
 * the custom.js script import line near the bottom of the file.
 *
 */

(async function () {
  // Below are example event listeners.  They listen for theme events that Pipeline
  // fires in order to make it easier for you to add customizations.

  // Keep your scripts inside this IIFE function call to avoid leaking your
  // variables into the global scope.

  const CURRENT_PARTNER_STORAGE_KEY = "CURRENT_PARTNER_STORAGE_KEY";
  const OVERRIDE_PARTNER_STORAGE_KEY = "OVERRIDE_PARTNER_STORAGE_KEY";
  const CURRENT_SHOP_AS_CUSTOMER_STORAGE_KEY =
    "CURRENT_SHOP_AS_CUSTOMER_STORAGE_KEY";
  const DEFAULT_CONSULTANT_DISCOUNT_RATE = 0.45;
  const JAFRA_VARIANT_METADATA_MAX_ATTEMPTS = 3;
  const JAFRA_VARIANT_METADATA_RETRY_BASE_MS = 250;
  const JAFRA_VARIANT_METADATA_MAX_CONCURRENT = 4;

  const centsToMoney = (cents) => ((Number(cents) || 0) / 100).toFixed(2);

  const traceJafraTaxThemeEvent = () => {};

  const safeJsonParseValue = (value) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch (e) {
      return null;
    }
  };

  const normalizeJafraVariantId = (variantId) => {
    if (variantId == null) return "";
    const value = String(variantId).split("/").pop();
    return value && /^\d+$/.test(value) ? value : "";
  };

  const jafraAvalaraVariantMetadataCache = {};
  const jafraAvalaraVariantMetadataRequestCache = {};
  const jafraAvalaraVariantMetadataQueue = [];
  let jafraAvalaraVariantMetadataActiveRequests = 0;

  const delayJafraAvalaraVariantMetadata = (ms) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

  const runQueuedJafraAvalaraVariantMetadataRequest = (task) =>
    new Promise((resolve) => {
      const run = () => {
        jafraAvalaraVariantMetadataActiveRequests += 1;
        task()
          .then(resolve)
          .finally(() => {
            jafraAvalaraVariantMetadataActiveRequests = Math.max(
              0,
              jafraAvalaraVariantMetadataActiveRequests - 1
            );
            const next = jafraAvalaraVariantMetadataQueue.shift();
            if (next) next();
          });
      };

      if (jafraAvalaraVariantMetadataActiveRequests < JAFRA_VARIANT_METADATA_MAX_CONCURRENT) {
        run();
        return;
      }

      jafraAvalaraVariantMetadataQueue.push(run);
    });

  const registerJafraAvalaraVariantMetadata = (variantId, metadata = {}) => {
    const normalizedVariantId = normalizeJafraVariantId(variantId);
    if (!normalizedVariantId || metadata.enabled === false) return;

    jafraAvalaraVariantMetadataCache[normalizedVariantId] = {
      ...(jafraAvalaraVariantMetadataCache[normalizedVariantId] || {}),
      ...metadata,
      enabled: true,
    };
  };

  const registerJafraAvalaraVariantSkip = (variantId, reason = "not_udf_eligible") => {
    const normalizedVariantId = normalizeJafraVariantId(variantId);
    if (!normalizedVariantId) return;

    jafraAvalaraVariantMetadataCache[normalizedVariantId] = {
      ...(jafraAvalaraVariantMetadataCache[normalizedVariantId] || {}),
      enabled: false,
      skipReason: reason,
    };
  };

  const getBooleanDatasetValue = (value, fallback = true) => {
    if (value === "false") return false;
    if (value === "true") return true;
    return fallback;
  };

  const getJafraAvalaraMetadataFromElement = (element) => {
    if (!element?.dataset || element.dataset.jafraAvalaraUdf !== "true") return null;

    return {
      enabled: true,
      retailCents: element.dataset.jafraRetailCents,
      discountEligible: getBooleanDatasetValue(element.dataset.jafraDiscountEligible, true),
      commissionable: element.dataset.quickAddToCartCommissionable,
      taxable: element.dataset.quickAddToCartTaxable,
    };
  };

  const indexRenderedJafraAvalaraVariants = () => {
    document.querySelectorAll("[data-add-to-cart-variant][data-jafra-avalara-udf='true']").forEach((element) => {
      registerJafraAvalaraVariantMetadata(element.getAttribute("data-add-to-cart-variant"), getJafraAvalaraMetadataFromElement(element));
    });

    document.querySelectorAll("[data-add-to-cart-variant]").forEach((element) => {
      if (element.dataset.jafraAvalaraUdf === "true") return;
      if (element.hasAttribute("data-kit-add-to-cart")) {
        registerJafraAvalaraVariantSkip(element.getAttribute("data-add-to-cart-variant"), "rendered_not_udf_eligible");
      }
    });

    document.querySelectorAll("form[data-jafra-avalara-udf]").forEach((form) => {
      const variantId = form.querySelector("[name='id']")?.value;
      if (form.dataset.jafraAvalaraUdf !== "true") {
        return;
      }

      const productJson = safeJsonParseValue(form.querySelector("[data-product-json]")?.textContent);
      const variant = productJson?.variants?.find((item) => String(item.id) === String(variantId));
      const commissionable = form.querySelector("[name='properties[_commissionable]']")?.value;
      const taxable = form.querySelector("[name='properties[_taxable]']")?.value;

      registerJafraAvalaraVariantMetadata(variantId, {
        enabled: true,
        retailCents: variant?.price,
        discountEligible: form.dataset.jafraDiscountEligible !== "false",
        commissionable,
        taxable,
      });
    });
  };

  const getJafraAvalaraVariantMetadata = (variantId, sourceElement) => {
    const normalizedVariantId = normalizeJafraVariantId(variantId);
    if (!normalizedVariantId) return null;

    const sourceMetadata = getJafraAvalaraMetadataFromElement(sourceElement);
    if (sourceMetadata?.retailCents) {
      registerJafraAvalaraVariantMetadata(normalizedVariantId, sourceMetadata);
      return sourceMetadata;
    }

    indexRenderedJafraAvalaraVariants();
    return jafraAvalaraVariantMetadataCache[normalizedVariantId] || null;
  };

  const fetchJafraAvalaraVariantMetadata = async (variantId) => {
    const normalizedVariantId = normalizeJafraVariantId(variantId);
    if (!normalizedVariantId) return null;

    const cachedMetadata = jafraAvalaraVariantMetadataCache[normalizedVariantId];
    if (cachedMetadata?.enabled === false) return cachedMetadata;
    if (cachedMetadata?.retailCents) return cachedMetadata;

    if (!jafraAvalaraVariantMetadataRequestCache[normalizedVariantId]) {
      jafraAvalaraVariantMetadataRequestCache[normalizedVariantId] = runQueuedJafraAvalaraVariantMetadataRequest(
        async () => {
          for (let attempt = 1; attempt <= JAFRA_VARIANT_METADATA_MAX_ATTEMPTS; attempt += 1) {
            const response = await window.fetch(`${window.theme?.routes?.root_url || "/"}variants/${normalizedVariantId}.js`, {
              method: "GET",
              headers: { Accept: "application/json" },
              credentials: "same-origin",
            });

            if (response.ok) {
              return response.json();
            }

            const shouldRetry =
              attempt < JAFRA_VARIANT_METADATA_MAX_ATTEMPTS &&
              (response.status === 429 || response.status >= 500);
            if (!shouldRetry) {
              throw new Error(`Variant metadata request failed with ${response.status}`);
            }

            const retryAfterMs = JAFRA_VARIANT_METADATA_RETRY_BASE_MS * attempt;
            traceJafraTaxThemeEvent("variant_metadata.retry", {
              variantId: normalizedVariantId,
              attempt,
              status: response.status,
              retryAfterMs,
            });
            await delayJafraAvalaraVariantMetadata(retryAfterMs);
          }

          throw new Error("Variant metadata request exhausted retries");
        }
      )
        .then((variant) => {
          if (!variant?.price) {
            throw new Error("Variant metadata response did not include price");
          }

          const metadata = {
            enabled: true,
            retailCents: variant.price,
            discountEligible: true,
            commissionable: "Commissionable",
            taxable: variant.taxable === false ? "false" : "true",
            sku: variant.sku,
            source: "variant_json",
          };

          registerJafraAvalaraVariantMetadata(normalizedVariantId, metadata);
          traceJafraTaxThemeEvent("variant_metadata.fetched", {
            variantId: normalizedVariantId,
            sku: metadata.sku,
            retailCents: metadata.retailCents,
            taxable: metadata.taxable,
          });
          return metadata;
        })
        .catch((error) => {
          delete jafraAvalaraVariantMetadataRequestCache[normalizedVariantId];
          traceJafraTaxThemeEvent("variant_metadata.fetch_failed", {
            variantId: normalizedVariantId,
            error: error?.message || String(error),
          });
          return null;
        });
    }

    return jafraAvalaraVariantMetadataRequestCache[normalizedVariantId];
  };

  const getJafraAvalaraVariantMetadataAsync = async (variantId, sourceElement) => {
    const metadata = getJafraAvalaraVariantMetadata(variantId, sourceElement);
    if (metadata?.enabled === false || metadata?.retailCents) return metadata;
    return fetchJafraAvalaraVariantMetadata(variantId);
  };

  const isJafraCartAddUrl = (url) => {
    const rawUrl = typeof url === "string" ? url : url?.url || "";
    if (!rawUrl) return false;

    try {
      const parsedUrl = new URL(rawUrl, window.location.origin);
      return parsedUrl.pathname === "/cart/add.js" || parsedUrl.pathname.endsWith("/cart/add.js");
    } catch (e) {
      return String(rawUrl).includes("/cart/add.js");
    }
  };

  const getJafraProperty = (properties, key) => properties?.[key] ?? properties?.[`properties[${key}]`];
  const JAFRA_AVALARA_UDF_PROPERTY_KEYS = ["_UDF1", "_UDF2", "_UDF3"];

  const hasJafraAvalaraUdfProperties = (properties) =>
    Boolean(getJafraProperty(properties, "_UDF1") && getJafraProperty(properties, "_UDF2") && getJafraProperty(properties, "_UDF3"));

  const removeJafraAvalaraUdfProperties = (properties = {}) => {
    let removed = false;
    JAFRA_AVALARA_UDF_PROPERTY_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(properties, key)) {
        delete properties[key];
        removed = true;
      }
      const bracketKey = `properties[${key}]`;
      if (Object.prototype.hasOwnProperty.call(properties, bracketKey)) {
        delete properties[bracketKey];
        removed = true;
      }
    });
    return removed;
  };

  const isJafraActualBundleCartAdd = (properties = {}) =>
    Boolean(
      getJafraProperty(properties, "_jafra_bundle_kind") ||
        getJafraProperty(properties, "_bundle_selection_ids") ||
        getJafraProperty(properties, "_bundle_selection_prices") ||
        getJafraProperty(properties, "_bundle_selection") ||
        getJafraProperty(properties, "_set_handle") ||
        getJafraProperty(properties, "_set_items") ||
        getJafraProperty(properties, "_set_type") ||
        getJafraProperty(properties, "_bundle") ||
        Object.keys(properties).some((key) => /^Product\s+\d+$/i.test(String(key).trim()))
    );

  const isJafraAvalaraUdfExemptCartAdd = (properties = {}) =>
    Boolean(
      getJafraProperty(properties, "_jafra_native_tax_probe") ||
        getJafraProperty(properties, "_freedrop") ||
        getJafraProperty(properties, "_vel2103_enrollment")
    );

  const isJafraNonRetailTitle = (title = "") => /-\s*non-retail\b/i.test(String(title || ""));

  const resolveJafraProductTitleFromSource = (sourceElement = null) => {
    if (!sourceElement) return "";
    const fromDataset =
      sourceElement.dataset?.productTitle ||
      sourceElement.getAttribute?.("data-product-title") ||
      "";
    if (fromDataset) return String(fromDataset);

    const form =
      sourceElement.tagName === "FORM"
        ? sourceElement
        : sourceElement.closest?.("form") || sourceElement.querySelector?.("form");
    if (!form) return "";

    const fromFormDataset = form.dataset?.productTitle || form.getAttribute?.("data-product-title") || "";
    if (fromFormDataset) return String(fromFormDataset);

    try {
      const productJson = safeJsonParseValue(form.querySelector?.("[data-product-json]")?.textContent);
      if (productJson?.title) return String(productJson.title);
    } catch (e) {
      /* ignore */
    }
    return "";
  };

  const resolveJafraCommissionableFromSource = (properties = {}, sourceElement = null) => {
    const fromProperties = getJafraProperty(properties, "_commissionable");
    if (fromProperties) return String(fromProperties);

    const fromDataset = sourceElement?.dataset?.quickAddToCartCommissionable;
    if (fromDataset) return String(fromDataset);

    const form =
      sourceElement?.tagName === "FORM"
        ? sourceElement
        : sourceElement?.closest?.("form") || sourceElement?.querySelector?.("form");
    const fromInput = form?.querySelector?.('[name="properties[_commissionable]"]')?.value;
    return fromInput ? String(fromInput) : "";
  };

  const isJafraNonCommissionableOrNonRetail = ({
    properties = {},
    productType = "",
    title = "",
    commissionableHint = "",
    sourceElement = null,
  } = {}) => {
    const commissionable = String(
      getJafraProperty(properties, "_commissionable") ||
        commissionableHint ||
        resolveJafraCommissionableFromSource(properties, sourceElement) ||
        ""
    )
      .trim()
      .toLowerCase();
    if (commissionable === "non-commissionable") return true;
    if (String(productType || "").trim().toLowerCase() === "non commissionable") return true;
    const resolvedTitle = title || resolveJafraProductTitleFromSource(sourceElement);
    if (isJafraNonRetailTitle(resolvedTitle)) return true;
    return false;
  };

  const shouldSkipJafraAvalaraUdfForSource = (properties = {}, sourceElement = null) => {
    if (sourceElement?.dataset?.jafraAvalaraUdf === "false") return true;
    const form =
      sourceElement?.tagName === "FORM"
        ? sourceElement
        : sourceElement?.closest?.("form") || sourceElement?.querySelector?.("form");
    if (form?.dataset?.jafraAvalaraUdf === "false") return true;
    return isJafraNonCommissionableOrNonRetail({ properties, sourceElement });
  };

  const isJafraBundleOrSpecialCartAdd = (properties = {}) =>
    Boolean(isJafraActualBundleCartAdd(properties) || isJafraAvalaraUdfExemptCartAdd(properties) || properties._promo_type);

  const applyJafraAvalaraUdfToProperties = (properties, variantId, sourceElement) => {
    const targetProperties = properties || {};
    if (!isJafraConsultantCustomer()) {
      const removedUdf = removeJafraAvalaraUdfProperties(targetProperties);
      return {
        properties: targetProperties,
        applied: false,
        removedUdf,
        reason: removedUdf ? "non_consultant_udf_removed" : "non_consultant_skip",
      };
    }

    if (
      shouldSkipJafraAvalaraUdfForSource(targetProperties, sourceElement)
    ) {
      const removedUdf = removeJafraAvalaraUdfProperties(targetProperties);
      return {
        properties: targetProperties,
        applied: false,
        removedUdf,
        reason: "non_commissionable_or_non_retail_skip",
      };
    }

    if (hasJafraAvalaraUdfProperties(targetProperties)) {
      return { properties: targetProperties, applied: false, reason: "already_ready" };
    }
    if (isJafraBundleOrSpecialCartAdd(targetProperties)) {
      return { properties: targetProperties, applied: false, reason: "special_skip" };
    }

    const metadata = getJafraAvalaraVariantMetadata(variantId, sourceElement);
    if (!metadata?.retailCents) {
      return { properties: targetProperties, applied: false, reason: "missing_metadata" };
    }

    if (
      isJafraNonCommissionableOrNonRetail({
        properties: targetProperties,
        commissionableHint: metadata.commissionable,
      })
    ) {
      const removedUdf = removeJafraAvalaraUdfProperties(targetProperties);
      return {
        properties: targetProperties,
        applied: false,
        removedUdf,
        reason: "non_commissionable_or_non_retail_skip",
      };
    }

    if (!targetProperties._commissionable && metadata.commissionable) {
      targetProperties._commissionable = metadata.commissionable;
    }
    if (!targetProperties._taxable && metadata.taxable) {
      targetProperties._taxable = metadata.taxable;
    }

    const udfProperties = buildJafraAvalaraUdfProperties({
      retailCents: metadata.retailCents,
      discountEligible: metadata.discountEligible !== false,
    });

    Object.assign(targetProperties, udfProperties);

    return {
      properties: targetProperties,
      applied: true,
      reason: "applied",
      sku: metadata.sku,
      udf1: udfProperties._UDF1,
      udf2: udfProperties._UDF2,
    };
  };

  const applyJafraAvalaraUdfToPropertiesAsync = async (properties, variantId, sourceElement) => {
    const targetProperties = properties || {};
    if (!isJafraConsultantCustomer()) {
      const removedUdf = removeJafraAvalaraUdfProperties(targetProperties);
      return {
        properties: targetProperties,
        applied: false,
        removedUdf,
        reason: removedUdf ? "non_consultant_udf_removed" : "non_consultant_skip",
      };
    }

    if (shouldSkipJafraAvalaraUdfForSource(targetProperties, sourceElement)) {
      const removedUdf = removeJafraAvalaraUdfProperties(targetProperties);
      return {
        properties: targetProperties,
        applied: false,
        removedUdf,
        reason: "non_commissionable_or_non_retail_skip",
      };
    }

    if (hasJafraAvalaraUdfProperties(targetProperties)) {
      return { properties: targetProperties, applied: false, reason: "already_ready" };
    }
    if (isJafraBundleOrSpecialCartAdd(targetProperties)) {
      return { properties: targetProperties, applied: false, reason: "special_skip" };
    }

    const metadata = await getJafraAvalaraVariantMetadataAsync(variantId, sourceElement);
    if (metadata?.enabled === false) {
      return { properties: targetProperties, applied: false, reason: metadata.skipReason || "not_udf_eligible" };
    }
    if (!metadata?.retailCents) {
      return { properties: targetProperties, applied: false, reason: "missing_metadata" };
    }

    if (
      isJafraNonCommissionableOrNonRetail({
        properties: targetProperties,
        commissionableHint: metadata.commissionable,
      })
    ) {
      const removedUdf = removeJafraAvalaraUdfProperties(targetProperties);
      return {
        properties: targetProperties,
        applied: false,
        removedUdf,
        reason: "non_commissionable_or_non_retail_skip",
      };
    }

    if (!targetProperties._commissionable && metadata.commissionable) {
      targetProperties._commissionable = metadata.commissionable;
    }
    if (!targetProperties._taxable && metadata.taxable) {
      targetProperties._taxable = metadata.taxable;
    }

    const udfProperties = buildJafraAvalaraUdfProperties({
      retailCents: metadata.retailCents,
      discountEligible: metadata.discountEligible !== false,
    });

    Object.assign(targetProperties, udfProperties);

    return {
      properties: targetProperties,
      applied: true,
      reason: metadata.source === "variant_json" ? "applied_async_variant_json" : "applied",
      sku: metadata.sku,
      udf1: udfProperties._UDF1,
      udf2: udfProperties._UDF2,
    };
  };

  const prepareJafraAvalaraCartAddItem = (item, sourceElement) => {
    if (!item || typeof item !== "object") return { applied: false, reason: "invalid_item" };

    const variantId = normalizeJafraVariantId(item.id || item.variant_id);
    if (!variantId) return { applied: false, reason: "missing_variant" };

    const result = applyJafraAvalaraUdfToProperties(item.properties || {}, variantId, sourceElement);
    if (result.applied || result.removedUdf) {
      item.properties = result.properties;
    }

    return {
      ...result,
      variantId,
    };
  };

  const prepareJafraAvalaraCartAddItemAsync = async (item, sourceElement) => {
    if (!item || typeof item !== "object") return { applied: false, reason: "invalid_item" };

    const variantId = normalizeJafraVariantId(item.id || item.variant_id);
    if (!variantId) return { applied: false, reason: "missing_variant" };

    const result = await applyJafraAvalaraUdfToPropertiesAsync(item.properties || {}, variantId, sourceElement);
    if (result.applied || result.removedUdf) {
      item.properties = result.properties;
    }

    return {
      ...result,
      variantId,
    };
  };

  const prepareJafraAvalaraCartAddJson = (payload, sourceElement) => {
    if (!payload || typeof payload !== "object") return { payload, appliedCount: 0, missingMetadata: [], results: [] };

    const items = Array.isArray(payload.items) ? payload.items : payload.id ? [payload] : [];
    const results = items.map((item) => prepareJafraAvalaraCartAddItem(item, sourceElement));
    const appliedCount = results.filter((result) => result.applied).length;
    const missingMetadata = results
      .filter((result) => result.reason === "missing_metadata")
      .map((result) => result.variantId)
      .filter(Boolean);

    return { payload, appliedCount, missingMetadata, results };
  };

  const prepareJafraAvalaraCartAddJsonAsync = async (payload, sourceElement) => {
    if (!payload || typeof payload !== "object") return { payload, appliedCount: 0, missingMetadata: [], results: [] };

    const items = Array.isArray(payload.items) ? payload.items : payload.id ? [payload] : [];
    const results = await Promise.all(items.map((item) => prepareJafraAvalaraCartAddItemAsync(item, sourceElement)));
    const appliedCount = results.filter((result) => result.applied).length;
    const missingMetadata = results
      .filter((result) => result.reason === "missing_metadata")
      .map((result) => result.variantId)
      .filter(Boolean);

    return { payload, appliedCount, missingMetadata, results };
  };

  const prepareJafraAvalaraCartAddSearchParams = (params, sourceElement) => {
    const variantId = params.get("id");
    const existingProperties = {};

    params.forEach((value, key) => {
      const propertyMatch = key.match(/^properties\[(.+)\]$/);
      if (propertyMatch) {
        existingProperties[propertyMatch[1]] = value;
      }
    });

    const result = applyJafraAvalaraUdfToProperties(existingProperties, variantId, sourceElement);
    if (result.applied || result.removedUdf) {
      JAFRA_AVALARA_UDF_PROPERTY_KEYS.forEach((key) => params.delete(`properties[${key}]`));
      Object.entries(result.properties).forEach(([key, value]) => {
        params.set(`properties[${key}]`, value);
      });
    }

    return {
      params,
      appliedCount: result.applied ? 1 : 0,
      missingMetadata: result.reason === "missing_metadata" ? [normalizeJafraVariantId(variantId)].filter(Boolean) : [],
      results: [{ ...result, variantId: normalizeJafraVariantId(variantId) }],
    };
  };

  const prepareJafraAvalaraCartAddSearchParamsAsync = async (params, sourceElement) => {
    const variantId = params.get("id");
    const existingProperties = {};

    params.forEach((value, key) => {
      const propertyMatch = key.match(/^properties\[(.+)\]$/);
      if (propertyMatch) {
        existingProperties[propertyMatch[1]] = value;
      }
    });

    const result = await applyJafraAvalaraUdfToPropertiesAsync(existingProperties, variantId, sourceElement);
    if (result.applied || result.removedUdf) {
      JAFRA_AVALARA_UDF_PROPERTY_KEYS.forEach((key) => params.delete(`properties[${key}]`));
      Object.entries(result.properties).forEach(([key, value]) => {
        params.set(`properties[${key}]`, value);
      });
    }

    return {
      params,
      appliedCount: result.applied ? 1 : 0,
      missingMetadata: result.reason === "missing_metadata" ? [normalizeJafraVariantId(variantId)].filter(Boolean) : [],
      results: [{ ...result, variantId: normalizeJafraVariantId(variantId) }],
    };
  };

  const prepareJafraAvalaraCartAddBody = (body, sourceElement) => {
    if (!body) return { body, appliedCount: 0, missingMetadata: [], results: [] };

    if (body instanceof URLSearchParams) {
      return {
        ...prepareJafraAvalaraCartAddSearchParams(body, sourceElement),
        body,
      };
    }

    if (body instanceof FormData) {
      const params = new URLSearchParams(body);
      const result = prepareJafraAvalaraCartAddSearchParams(params, sourceElement);
      JAFRA_AVALARA_UDF_PROPERTY_KEYS.forEach((key) => body.delete(`properties[${key}]`));
      result.params.forEach((value, key) => body.set(key, value));
      return { body, appliedCount: result.appliedCount, missingMetadata: result.missingMetadata, results: result.results };
    }

    if (typeof body === "string") {
      const trimmedBody = body.trim();
      const jsonPayload = trimmedBody.startsWith("{") ? safeJsonParseValue(trimmedBody) : null;
      if (jsonPayload) {
        const result = prepareJafraAvalaraCartAddJson(jsonPayload, sourceElement);
        return {
          body: JSON.stringify(result.payload),
          appliedCount: result.appliedCount,
          missingMetadata: result.missingMetadata,
          results: result.results,
        };
      }

      const params = new URLSearchParams(body);
      const result = prepareJafraAvalaraCartAddSearchParams(params, sourceElement);
      return {
        body: result.params.toString(),
        appliedCount: result.appliedCount,
        missingMetadata: result.missingMetadata,
        results: result.results,
      };
    }

    if (typeof body === "object") {
      const result = prepareJafraAvalaraCartAddJson(body, sourceElement);
      return {
        body: result.payload,
        appliedCount: result.appliedCount,
        missingMetadata: result.missingMetadata,
        results: result.results,
      };
    }

    return { body, appliedCount: 0, missingMetadata: [], results: [] };
  };

  const prepareJafraAvalaraCartAddBodyAsync = async (body, sourceElement) => {
    if (!body) return { body, appliedCount: 0, missingMetadata: [], results: [] };

    if (body instanceof URLSearchParams) {
      return {
        ...(await prepareJafraAvalaraCartAddSearchParamsAsync(body, sourceElement)),
        body,
      };
    }

    if (body instanceof FormData) {
      const params = new URLSearchParams(body);
      const result = await prepareJafraAvalaraCartAddSearchParamsAsync(params, sourceElement);
      JAFRA_AVALARA_UDF_PROPERTY_KEYS.forEach((key) => body.delete(`properties[${key}]`));
      result.params.forEach((value, key) => body.set(key, value));
      return { body, appliedCount: result.appliedCount, missingMetadata: result.missingMetadata, results: result.results };
    }

    if (typeof body === "string") {
      const trimmedBody = body.trim();
      const jsonPayload = trimmedBody.startsWith("{") ? safeJsonParseValue(trimmedBody) : null;
      if (jsonPayload) {
        const result = await prepareJafraAvalaraCartAddJsonAsync(jsonPayload, sourceElement);
        return {
          body: JSON.stringify(result.payload),
          appliedCount: result.appliedCount,
          missingMetadata: result.missingMetadata,
          results: result.results,
        };
      }

      const params = new URLSearchParams(body);
      const result = await prepareJafraAvalaraCartAddSearchParamsAsync(params, sourceElement);
      return {
        body: result.params.toString(),
        appliedCount: result.appliedCount,
        missingMetadata: result.missingMetadata,
        results: result.results,
      };
    }

    if (typeof body === "object") {
      const result = await prepareJafraAvalaraCartAddJsonAsync(body, sourceElement);
      return {
        body: result.payload,
        appliedCount: result.appliedCount,
        missingMetadata: result.missingMetadata,
        results: result.results,
      };
    }

    return { body, appliedCount: 0, missingMetadata: [], results: [] };
  };

  window.jafraPrepareAvalaraCartAddPayload = (payload, sourceElement) => {
    const result = prepareJafraAvalaraCartAddJson(payload, sourceElement);
    return result.payload;
  };

  const summarizeJafraCartAddResults = (results = []) =>
    results.map((result) => ({
      variantId: result.variantId,
      applied: result.applied,
      reason: result.reason,
      udf1: result.udf1,
      udf2: result.udf2,
    }));

  const normalizeJafraDiscountRate = (rate) => {
    const numericRate = Number(rate);
    if (!Number.isFinite(numericRate) || numericRate <= 0) return 0;
    return numericRate > 1 ? numericRate / 100 : numericRate;
  };

  const isJafraConsultantCustomer = () =>
    window.customer?.userType === "Consultant" || window.customer?.isPartner === true;

  const getJafraCustomerDiscountRate = () => {
    try {
      const isImp = sessionStorage.getItem('jafra_is_impersonated') === 'true' || localStorage.getItem('jafra_is_impersonated') === 'true';
      if (isImp) {
        const impComm = sessionStorage.getItem('jafra_impersonated_commission_rate') || localStorage.getItem('jafra_impersonated_commission_rate');
        if (impComm && !isNaN(Number(impComm)) && Number(impComm) > 0) {
          return normalizeJafraDiscountRate(impComm);
        }
      }
    } catch (e) {}

    const customerDiscountRate = normalizeJafraDiscountRate(window.customerDiscountRate);
    if (customerDiscountRate > 0) return customerDiscountRate;

    try {
      const snizeDiscountRate = normalizeJafraDiscountRate(snizeComissionPercent);
      if (snizeDiscountRate > 0) return snizeDiscountRate;
    } catch (e) {
      // Keep falling back below when Searchanise's global is not available.
    }

    return isJafraConsultantCustomer() ? DEFAULT_CONSULTANT_DISCOUNT_RATE : 0;
  };

  const getJafraCustomerOrderType = () => {
    if (window.customer?.userType) return window.customer.userType;
    if (window.customer?.id) return "Retail";
    return "Guest";
  };

  const buildJafraAvalaraUdfProperties = ({
    retailCents,
    discountEligible = true,
  }) => {
    const retail = Number(retailCents) || 0;
    const discountRate = discountEligible ? getJafraCustomerDiscountRate() : 0;
    const discounted = Math.round(retail * (1 - discountRate));

    return {
      _UDF1: centsToMoney(retail),
      _UDF2: centsToMoney(discounted),
      _UDF3: getJafraCustomerOrderType(),
    };
  };

  const findJafraProductVariant = (form, variantId) => {
    const productJsonEl = form?.querySelector("[data-product-json]");
    const productJson = safeJsonParseValue(productJsonEl?.textContent);
    if (!productJson?.variants?.length) return null;

    return productJson.variants.find((variant) => String(variant.id) === String(variantId));
  };

  const setJafraFormProperty = (form, key, value) => {
    let input = form.querySelector(`[name="properties[${key}]"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${key}]`;
      form.appendChild(input);
    }
    input.value = value;
  };

  const removeJafraFormUdfProperties = (form) => {
    JAFRA_AVALARA_UDF_PROPERTY_KEYS.forEach((key) => {
      form?.querySelector(`[name="properties[${key}]"]`)?.remove();
    });
  };

  window.jafraApplyAvalaraUdfToProductForm = (form) => {
    if (!form) return;

    const formData = new FormData(form);
    const seedProperties = {
      _commissionable: formData.get("properties[_commissionable]") || "",
      _taxable: formData.get("properties[_taxable]") || "",
      _jafra_bundle_kind: formData.get("properties[_jafra_bundle_kind]") || form.dataset.jafraBundleKind || "",
    };

    // Always strip UDF for Non Commissionable / Non-Retail before any stamp path.
    if (shouldSkipJafraAvalaraUdfForSource(seedProperties, form)) {
      removeJafraFormUdfProperties(form);
      traceJafraTaxThemeEvent("product_form.prepare", {
        applied: false,
        reason: "non_commissionable_or_non_retail_skip",
      });
      return;
    }

    // Liquid-first bundles: kind + fixed + lookup are stamped on the form by Liquid.
    // jafra-bundle-udf.js reads customer picks and writes selection + _UDF1.
    if (form?.dataset?.jafraBundleKind && window.jafraBundleUdf?.prepareProductForm) {
      const result = window.jafraBundleUdf.prepareProductForm(form);
      traceJafraTaxThemeEvent("product_form.bundle_prepare", {
        kind: form.dataset.jafraBundleKind,
        handled: Boolean(result?.handled),
        udf1: result?.udf1 || "",
      });
      return;
    }

    if (form.dataset.jafraAvalaraUdf !== "true") {
      removeJafraFormUdfProperties(form);
      return;
    }
    if (!isJafraConsultantCustomer()) {
      removeJafraFormUdfProperties(form);
      traceJafraTaxThemeEvent("product_form.prepare", {
        applied: false,
        reason: "non_consultant_skip",
      });
      return;
    }

    const variant = findJafraProductVariant(form, formData.get("id"));
    registerJafraAvalaraVariantMetadata(formData.get("id"), {
      enabled: true,
      retailCents: variant?.price,
      discountEligible: form.dataset.jafraDiscountEligible !== "false",
      commissionable: seedProperties._commissionable,
      taxable: seedProperties._taxable,
    });

    const result = applyJafraAvalaraUdfToProperties(seedProperties, formData.get("id"), form);
    traceJafraTaxThemeEvent("product_form.prepare", {
      variantId: normalizeJafraVariantId(formData.get("id")),
      applied: result.applied,
      reason: result.reason,
      udf1: result.udf1,
      udf2: result.udf2,
    });
    if (!result.applied) {
      if (result.removedUdf) removeJafraFormUdfProperties(form);
      return;
    }

    Object.entries(result.properties).forEach(([key, value]) => {
      if (!JAFRA_AVALARA_UDF_PROPERTY_KEYS.includes(key)) return;
      setJafraFormProperty(form, key, value);
    });
  };

  window.jafraAppendAvalaraUdfToFormString = (formString, sourceElement) => {
    const params = new URLSearchParams(formString);
    const form =
      sourceElement?.closest?.("form[action*='/cart/add']") ||
      sourceElement?.querySelector?.("form[action*='/cart/add']") ||
      document.querySelector("form[data-jafra-bundle-kind]");
    if (form?.dataset?.jafraBundleKind && window.jafraBundleUdf?.prepareCartAddSearchParams) {
      const existing = {};
      params.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) existing[match[1]] = value;
      });
      const variantId = params.get("id");
      const variant = findJafraProductVariant(form, variantId);
      // _UDF1 is always unit sale price (never line total / qty).
      const parentPriceCents = variant?.price != null ? Number(variant.price) : 0;
      const liquidFirst = window.jafraBundleUdf.prepareCartAddSearchParams(
        params,
        form,
        existing,
        { parentPriceCents },
      );
      if (liquidFirst?.handled) {
        traceJafraTaxThemeEvent("form_string.bundle_prepare", {
          kind: liquidFirst.kind,
          udf1: liquidFirst.udf1 || "",
        });
        return params.toString();
      }
    }

    const result = prepareJafraAvalaraCartAddSearchParams(params, sourceElement);
    traceJafraTaxThemeEvent("form_string.prepare", {
      variantId: normalizeJafraVariantId(params.get("id")),
      appliedCount: result.appliedCount,
      results: summarizeJafraCartAddResults(result.results),
    });
    return result.params.toString();
  };

  /**
   * DCatalog / brochure: fetch product HTML, read the same Liquid markers
   * via jafraBundleUdf.prepareFromDocument, return enriched postData for /cart/add.js.
   */
  window.jafraPrepareEcatalogBundleCartPostDataAsync = async (postData = {}, oData = {}) => {
    const normalized = {
      ...postData,
      properties: { ...(postData.properties || {}) },
    };
    if (oData.handle && !normalized.properties._set_handle) {
      normalized.properties._set_handle = oData.handle;
    }

    const setHandle = String(normalized.properties._set_handle || oData.handle || "").trim();
    const hasBundleSignal =
      Boolean(normalized.properties._jafra_bundle_kind) ||
      Boolean(setHandle) ||
      Object.keys(normalized.properties).some((key) =>
        /^(Product|Choice|Choose|Selection)\b/i.test(String(key).trim()),
      );
    if (!hasBundleSignal || !setHandle || typeof window.jafraBundleUdf?.prepareFromDocument !== "function") {
      return normalized;
    }

    let parentPriceCents = 0;
    let html = "";
    try {
      const productRes = await fetch(`/products/${encodeURIComponent(setHandle)}.js`);
      if (productRes.ok) {
        const productJson = await productRes.json();
        const variant = (productJson.variants || []).find(
          (item) => String(item.id) === String(normalized.id),
        );
        // _UDF1 allocates the unit PDP sale price, not qty * unit.
        if (variant?.price != null) parentPriceCents = Math.round(Number(variant.price));
      }
      const pageRes = await fetch(`/products/${encodeURIComponent(setHandle)}`);
      if (pageRes.ok) html = await pageRes.text();
    } catch (error) {
      console.warn("jafraPrepareEcatalogBundleCartPostDataAsync fetch failed", error);
      return normalized;
    }

    if (!html) return normalized;
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const liquidFirst = window.jafraBundleUdf.prepareFromDocument(
        doc,
        normalized.properties,
        parentPriceCents,
      );
      if (!liquidFirst?.handled) return normalized;
      normalized.properties = liquidFirst.properties;
      if (liquidFirst.kind === "simple") {
        delete normalized.properties._bundle_selection;
        delete normalized.properties._bundle_selection_ids;
        delete normalized.properties._bundle_selection_prices;
      }
      delete normalized.properties._UDF2;
      traceJafraTaxThemeEvent("ecatalog.bundle_prepare", {
        handle: setHandle,
        kind: liquidFirst.kind,
        udf1: liquidFirst.udf1 || "",
      });
    } catch (error) {
      console.warn("jafraPrepareEcatalogBundleCartPostDataAsync prepare failed", error);
    }
    return normalized;
  };

  window.jafraBuildAvalaraUdfPropertiesFromElement = (sourceElement) => {
    if (!isJafraConsultantCustomer()) {
      traceJafraTaxThemeEvent("element_properties.prepare", {
        applied: false,
        reason: "non_consultant_skip",
      });
      return {};
    }

    if (shouldSkipJafraAvalaraUdfForSource({}, sourceElement)) {
      traceJafraTaxThemeEvent("element_properties.prepare", {
        applied: false,
        reason: "non_commissionable_or_non_retail_skip",
      });
      return {};
    }

    const variantId = sourceElement?.dataset?.addToCartVariant;
    const metadata = getJafraAvalaraVariantMetadata(variantId, sourceElement);
    if (!metadata?.retailCents) {
      traceJafraTaxThemeEvent("element_properties.prepare", {
        variantId: normalizeJafraVariantId(variantId),
        applied: false,
        reason: "missing_metadata",
      });
      return {};
    }

    if (
      isJafraNonCommissionableOrNonRetail({
        commissionableHint: metadata.commissionable,
      })
    ) {
      traceJafraTaxThemeEvent("element_properties.prepare", {
        variantId: normalizeJafraVariantId(variantId),
        applied: false,
        reason: "non_commissionable_or_non_retail_skip",
      });
      return {};
    }

    const properties = buildJafraAvalaraUdfProperties({
      retailCents: metadata.retailCents,
      discountEligible: metadata.discountEligible !== false,
    });
    traceJafraTaxThemeEvent("element_properties.prepare", {
      variantId: normalizeJafraVariantId(variantId),
      applied: true,
      udf1: properties._UDF1,
      udf2: properties._UDF2,
    });
    return properties;
  };

  const hasAnyJafraAvalaraUdfProperty = (properties = {}) =>
    JAFRA_AVALARA_UDF_PROPERTY_KEYS.some((key) => Boolean(getJafraProperty(properties, key)));

  const jafraCartLineUdfSignature = (properties = {}) =>
    JAFRA_AVALARA_UDF_PROPERTY_KEYS.map(
      (key) => `${key}=${String(getJafraProperty(properties, key) || "").trim()}`
    ).join("|");

  const stripJafraUdfFromLineProperties = (properties = {}) => {
    const next = { ...(properties || {}) };
    removeJafraAvalaraUdfProperties(next);
    return next;
  };

  const isJafraCheckoutUdfExemptLine = (item = {}) => {
    const productType = String(item.product_type || "").trim().toLowerCase();
    const sku = String(item.sku || "").trim().toUpperCase();
    const properties = item.properties || {};
    if (productType === "gift card") return true;
    if (sku === "TAX" || sku === "CHARGE_MISC" || sku === "21047") return true;
    if (isJafraAvalaraUdfExemptCartAdd(properties)) return true;
    if (getJafraProperty(properties, "_promo_type")) return true;
    if (getJafraProperty(properties, "_bogos") || getJafraProperty(properties, "_bogo")) return true;
    if (
      isJafraNonCommissionableOrNonRetail({
        properties,
        productType: item.product_type,
        title: item.product_title || item.title,
      })
    ) {
      return true;
    }
    return false;
  };

  const resolveJafraCartLineRetailCents = (item = {}) => {
    const quantity = Math.max(Number(item.quantity) || 1, 1);
    const candidates = [
      item.original_price,
      item.price,
      item.discounted_price,
      item.final_price,
      Number(item.original_line_price) / quantity,
      Number(item.final_line_price) / quantity,
      Number(item.line_price) / quantity,
    ];

    for (const candidate of candidates) {
      const cents = Math.round(Number(candidate));
      if (Number.isFinite(cents) && cents > 0) return cents;
    }

    for (const candidate of candidates) {
      const cents = Math.round(Number(candidate));
      if (Number.isFinite(cents) && cents === 0) return 0;
    }

    return null;
  };

  const lineHasRequiredCheckoutUdf = (properties = {}, isBundle = false) => {
    const udf1 = getJafraProperty(properties, "_UDF1");
    const udf2 = getJafraProperty(properties, "_UDF2");
    const udf3 = getJafraProperty(properties, "_UDF3");
    if (isBundle) return Boolean(udf1 && udf3 && !udf2);
    return Boolean(udf1 && udf2 && udf3);
  };

  const resolveJafraCheckoutParentPriceCents = (item = {}) => {
    // Bundle/single _UDF1 must use unit retail/sale cents, never line total.
    const unitPriceCents = Number(item.original_price);
    if (Number.isFinite(unitPriceCents) && unitPriceCents > 0) {
      return Math.round(unitPriceCents);
    }
    const quantity = Math.max(Number(item.quantity) || 1, 1);
    const originalLinePriceCents = Number(item.original_line_price);
    if (Number.isFinite(originalLinePriceCents) && originalLinePriceCents > 0 && quantity > 0) {
      return Math.round(originalLinePriceCents / quantity);
    }
    return 0;
  };

  const resolveJafraCheckoutBundlePropertiesAsync = async (item = {}, properties = {}) => {
    const setHandle = String(getJafraProperty(properties, "_set_handle") || "").trim();
    if (!setHandle || typeof window.jafraBundleUdf?.prepareFromDocument !== "function") {
      return { applied: false, reason: "bundle_missing_liquid_context" };
    }

    try {
      const pageRes = await fetch(`/products/${encodeURIComponent(setHandle)}`);
      if (!pageRes.ok) {
        return { applied: false, reason: "bundle_product_fetch_failed" };
      }
      const html = await pageRes.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const result = window.jafraBundleUdf.prepareFromDocument(
        doc,
        properties,
        resolveJafraCheckoutParentPriceCents(item)
      );
      if (!result?.handled || !result.udf1) {
        return { applied: false, reason: result?.handled ? "bundle_udf1_unresolved" : "bundle_prepare_unhandled" };
      }
      return {
        applied: true,
        reason: "bundle_udf1_stamped",
        properties: result.properties,
        udf1: result.udf1,
      };
    } catch (error) {
      return { applied: false, reason: "bundle_prepare_failed", error: error?.message || String(error) };
    }
  };

  const resolveJafraCheckoutDesiredLinePropertiesAsync = async (item = {}) => {
    const variantId = normalizeJafraVariantId(item.variant_id || item.id);
    const currentProperties = { ...(item.properties || {}) };
    const isConsultant = isJafraConsultantCustomer();
    const isExempt = isJafraCheckoutUdfExemptLine(item);
    const orderType = getJafraCustomerOrderType();

    if (!isConsultant || isExempt) {
      if (!hasAnyJafraAvalaraUdfProperty(currentProperties)) {
        return {
          properties: currentProperties,
          changed: false,
          reason: isExempt ? "exempt_clean" : "non_consultant_clean",
          variantId,
          required: false,
          udfReady: true,
        };
      }
      const stripped = stripJafraUdfFromLineProperties(currentProperties);
      return {
        properties: stripped,
        changed: jafraCartLineUdfSignature(currentProperties) !== jafraCartLineUdfSignature(stripped),
        reason: isExempt ? "exempt_strip" : "non_consultant_strip",
        variantId,
        required: false,
        udfReady: true,
      };
    }

    const isBundle = isJafraActualBundleCartAdd(currentProperties);
    const existingUdf1 = getJafraProperty(currentProperties, "_UDF1");
    const existingUdf2 = getJafraProperty(currentProperties, "_UDF2");
    const existingUdf3 = getJafraProperty(currentProperties, "_UDF3");

    if (isBundle) {
      if (existingUdf1) {
        const nextProperties = { ...currentProperties };
        delete nextProperties._UDF2;
        nextProperties._UDF3 = orderType;
        return {
          properties: nextProperties,
          changed: jafraCartLineUdfSignature(currentProperties) !== jafraCartLineUdfSignature(nextProperties),
          reason: "bundle_udf1_keep_from_atc",
          variantId,
          udf1: getJafraProperty(nextProperties, "_UDF1") || "",
          udf2: "",
          udf3: getJafraProperty(nextProperties, "_UDF3") || "",
          isBundle: true,
          required: true,
          udfReady: lineHasRequiredCheckoutUdf(nextProperties, true),
        };
      }

      const cleaned = stripJafraUdfFromLineProperties(currentProperties);
      const result = await resolveJafraCheckoutBundlePropertiesAsync(item, cleaned);
      if (!result.applied) {
        return {
          properties: currentProperties,
          changed: false,
          reason: result.reason || "bundle_udf1_unresolved",
          variantId,
          isBundle: true,
          required: true,
          udfReady: false,
          fatal: true,
        };
      }

      const nextProperties = { ...(result.properties || cleaned) };
      delete nextProperties._UDF2;
      nextProperties._UDF3 = orderType;
      return {
        properties: nextProperties,
        changed: true,
        reason: result.reason || "bundle_udf1_stamped",
        variantId,
        udf1: getJafraProperty(nextProperties, "_UDF1") || "",
        udf2: "",
        udf3: getJafraProperty(nextProperties, "_UDF3") || "",
        isBundle: true,
        required: true,
        udfReady: lineHasRequiredCheckoutUdf(nextProperties, true),
      };
    }

    if (existingUdf1 && existingUdf2 && existingUdf3 === orderType) {
      return {
        properties: currentProperties,
        changed: false,
        reason: "single_already_ready",
        variantId,
        udf1: existingUdf1,
        udf2: existingUdf2,
        udf3: existingUdf3,
        isBundle: false,
        required: true,
        udfReady: true,
      };
    }

    const cleaned = stripJafraUdfFromLineProperties(currentProperties);
    const result = await applyJafraAvalaraUdfToPropertiesAsync(cleaned, variantId, null);
    if (result.applied) {
      return {
        properties: result.properties,
        changed: true,
        reason: result.reason || "single_udf_stamped",
        variantId,
        udf1: getJafraProperty(result.properties, "_UDF1") || "",
        udf2: getJafraProperty(result.properties, "_UDF2") || "",
        udf3: getJafraProperty(result.properties, "_UDF3") || "",
        isBundle: false,
        required: true,
        udfReady: lineHasRequiredCheckoutUdf(result.properties, false),
      };
    }

    // Cart/upsell adds often skip ATC UDF stamping and /variants/{id}.js can miss
    // metadata on the cart page. Fall back to cart line retail (cents).
    const cartRetailCents = resolveJafraCartLineRetailCents(item);
    if (cartRetailCents != null && cartRetailCents >= 0) {
      const discountEligible =
        cartRetailCents > 0 &&
        String(getJafraProperty(currentProperties, "_commissionable") || "").toLowerCase() !==
          "non-commissionable" &&
        String(item.product_type || "").trim().toLowerCase() !== "gift card";
      const udfValues =
        cartRetailCents === 0
          ? {
              _UDF1: "0.00",
              _UDF2: "0.00",
              _UDF3: orderType,
            }
          : buildJafraAvalaraUdfProperties({
              retailCents: cartRetailCents,
              discountEligible,
            });
      const nextProperties = { ...cleaned, ...udfValues };
      return {
        properties: nextProperties,
        changed: true,
        reason:
          cartRetailCents === 0
            ? "single_udf_stamped_from_cart_zero"
            : "single_udf_stamped_from_cart_price",
        variantId,
        udf1: getJafraProperty(nextProperties, "_UDF1") || "",
        udf2: getJafraProperty(nextProperties, "_UDF2") || "",
        udf3: getJafraProperty(nextProperties, "_UDF3") || "",
        isBundle: false,
        required: true,
        udfReady: lineHasRequiredCheckoutUdf(nextProperties, false),
      };
    }

    return {
      properties: currentProperties,
      changed: false,
      reason: result.reason || "single_udf_unresolved",
      variantId,
      isBundle: false,
      required: true,
      udfReady: false,
      fatal: true,
      cartPriceDebug: {
        original_price: item.original_price,
        price: item.price,
        discounted_price: item.discounted_price,
        final_price: item.final_price,
        original_line_price: item.original_line_price,
        final_line_price: item.final_line_price,
        line_price: item.line_price,
        quantity: item.quantity,
        product_type: item.product_type,
        title: item.product_title || item.title,
        propertyKeys: Object.keys(currentProperties || {}),
      },
    };
  };

  const prepareJafraCartUdfBeforeCheckoutAsync = async (inputCart = null) => {
    const fetchCart = async () =>
      fetch(`/cart.js?t=${Date.now()}`).then((response) => response.json());

    let cart = inputCart || (await fetchCart());
    const results = [];
    const isConsultant = isJafraConsultantCustomer();
    const maxPasses = Math.max((cart?.items?.length || 0) * 2 + 2, 4);

    traceJafraTaxThemeEvent("checkout.udf_preflight.start", {
      itemCount: cart?.items?.length || 0,
      isConsultant,
    });

    for (let pass = 0; pass < maxPasses; pass += 1) {
      cart = pass === 0 && inputCart ? cart : await fetchCart();
      let mutated = false;

      for (let index = 0; index < (cart?.items?.length || 0); index += 1) {
        const item = cart.items[index];
        if (!item?.key) {
          results.push({
            index,
            changed: false,
            reason: "missing_line_key",
            variantId: normalizeJafraVariantId(item?.variant_id || item?.id),
            fatal: true,
          });
          continue;
        }

        let desired;
        try {
          desired = await resolveJafraCheckoutDesiredLinePropertiesAsync(item);
        } catch (error) {
          results.push({
            index,
            changed: false,
            reason: "resolve_failed",
            variantId: normalizeJafraVariantId(item.variant_id || item.id),
            error: error?.message || String(error),
            fatal: true,
          });
          continue;
        }

        if (!desired.changed) {
          results.push({
            index,
            changed: false,
            reason: desired.reason,
            variantId: desired.variantId,
            udf1: desired.udf1 || getJafraProperty(item.properties || {}, "_UDF1") || "",
            isBundle: desired.isBundle || false,
            udfReady: desired.udfReady !== false,
            fatal: Boolean(desired.fatal),
            ...(desired.cartPriceDebug ? { cartPriceDebug: desired.cartPriceDebug } : {}),
          });
          continue;
        }

        try {
          const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity || 1,
              properties: desired.properties,
            }),
          });
          if (!response.ok) {
            const errorText = await response.text();
            results.push({
              index,
              changed: false,
              reason: "cart_change_failed",
              variantId: desired.variantId,
              status: response.status,
              error: errorText.slice(0, 200),
              fatal: true,
            });
            continue;
          }
          cart = await response.json();
          mutated = true;
          results.push({
            index,
            changed: true,
            reason: desired.reason,
            variantId: desired.variantId,
            udf1: desired.udf1 || "",
            udf2: desired.udf2 || "",
            udf3: desired.udf3 || "",
            isBundle: desired.isBundle || false,
            udfReady: desired.udfReady !== false,
          });
          break;
        } catch (error) {
          results.push({
            index,
            changed: false,
            reason: "cart_change_failed",
            variantId: desired.variantId,
            error: error?.message || String(error),
            fatal: true,
          });
        }
      }

      if (!mutated) break;
    }

    cart = await fetchCart();
    let ok = true;
    const validation = [];

    for (const item of cart?.items || []) {
      const properties = item.properties || {};
      const isExempt = isJafraCheckoutUdfExemptLine(item);
      const isBundle = isJafraActualBundleCartAdd(properties);
      const hasUdf = hasAnyJafraAvalaraUdfProperty(properties);

      if (!isConsultant || isExempt) {
        if (hasUdf) {
          ok = false;
          validation.push({
            variantId: normalizeJafraVariantId(item.variant_id || item.id),
            reason: "udf_still_present",
          });
        }
        continue;
      }

      if (!lineHasRequiredCheckoutUdf(properties, isBundle)) {
        ok = false;
        validation.push({
          variantId: normalizeJafraVariantId(item.variant_id || item.id),
          reason: isBundle ? "bundle_udf_incomplete" : "single_udf_incomplete",
          udf1: getJafraProperty(properties, "_UDF1") || "",
          udf2: getJafraProperty(properties, "_UDF2") || "",
          udf3: getJafraProperty(properties, "_UDF3") || "",
        });
      }
    }

    if (results.some((result) => result.fatal)) ok = false;

    const changedCount = results.filter((result) => result.changed).length;
    traceJafraTaxThemeEvent("checkout.udf_preflight.done", {
      itemCount: cart?.items?.length || 0,
      changedCount,
      isConsultant,
      ok,
      validation,
      results: results.map((result) => ({
        variantId: result.variantId || "",
        changed: Boolean(result.changed),
        reason: result.reason || "",
        udf1: result.udf1 || "",
        isBundle: Boolean(result.isBundle),
        fatal: Boolean(result.fatal),
      })),
    });

    return { cart, results, changedCount, ok, validation };
  };

  window.jafraPrepareCartUdfBeforeCheckoutAsync = prepareJafraCartUdfBeforeCheckoutAsync;

  const storedCustomerKey = sessionStorage.getItem(
    CURRENT_SHOP_AS_CUSTOMER_STORAGE_KEY
  );

  document.addEventListener("theme:cart:change", async function (event) {
    console.log("========= theme:cart:change =========");
    var cart = event.detail.cart;

    var startTime = performance.now();
    configureCustomCheckout(cart);
    var endTime = performance.now();
    console.log(
      "configureCustomCheckout took " + (endTime - startTime) + " milliseconds."
    );
    
    // Update VIP tracker
    updateVIPTracker(cart);
  });

  function listenFetch() {
    var oriFetch = window.fetch;

    window.fetch = async (url, options) => {
      // if (url?.href?.includes("?sca_") || (typeof url === "string" && url.includes("?sca_"))) {
      //   return;
      // }

      // // fixes cart refresh issue for bogo gifts
      // if (["/cart/update?sca_process_gifts"].includes(url)) {
      //   return;
      // }

      let requestOptions = options;
      try {
        if (isJafraCartAddUrl(url) && options?.body) {
          const prepared = await prepareJafraAvalaraCartAddBodyAsync(options.body);
          if (prepared?.body && prepared.body !== options.body) {
            requestOptions = { ...options, body: prepared.body };
            traceJafraTaxThemeEvent("cart_add.fetch.prepared", {
              appliedCount: prepared.appliedCount,
              missingMetadata: prepared.missingMetadata,
              results: summarizeJafraCartAddResults(prepared.results || []),
            });
          }
        }
      } catch (error) {
        traceJafraTaxThemeEvent("cart_add.fetch.prepare_failed", {
          error: error?.message || String(error),
        });
      }

      return oriFetch(url, requestOptions).then(async (res) => {
        return res;
      });
    };
  }

  listenFetch();

  const kitCheck = (item) => { return item.product_type === "Kit" };

  async function configureCustomCheckout(cart) {
    console.log("========= congigureCustomCheckout start =========");
    window.FREEDROP_IN_PROGRESS = false;
    const customCheckout = $("[data-checkout-custom]");
    const checkoutError = $("[data-checkout-error]");
    var disableCheckout = false;

    $(document.body).removeClass("loading-checkout");
    customCheckout.prop("disabled", true);

    let shopAsCustomer = null;

    if (storedCustomerKey) {
      shopAsCustomer = await fetch(
        `https://st8jv6iz98.execute-api.us-west-2.amazonaws.com/STG2/partners?website=${storedCustomerKey}`
      )
        .then((response) => response.json())
        .then((response) => {
          return response;
        })
        .catch((err) => console.error(err));
    }

    if (cart && !shopAsCustomer) {
      const foundNonCommissionableItem = cart.items.find(
        (item) =>
          item.properties["_commissionable"] === "Non-Commissionable" &&
          kitCheck(item) === false
      );

      if (customCheckout.is("[data-invalid-standing]")) {
        disableCheckout = true;
      } else if (
        foundNonCommissionableItem &&
        !theme.is_partner &&
        foundNonCommissionableItem.product_type !== "Gift Card"
      ) {
        if (checkoutError) checkoutError.show();
        disableCheckout = true;
      } else {
        if (checkoutError) checkoutError.hide();
        disableCheckout = false;
      }
    }

    customCheckout.prop("disabled", disableCheckout);
    customCheckout.text(window.theme.strings.proceedToCheckout);

    customCheckout.unbind("click").bind("click", async (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (customCheckout.is("[data-invalid-standing]")) {
        customCheckout.prop("disabled", true);
        return;
      }

      console.log("========= customCheckout clicked =========");

      // Freedrop ////////////////////////////////////////////////////////////////////
      if (window.FREEDROP_IN_PROGRESS) return;
      window.FREEDROP_IN_PROGRESS = true;

      $(document.body).addClass("loading-checkout");
      let cart = await fetch(`/cart.js?t=${Date.now()}`).then((response) => response.json());
      const existingFreeItems = cart.items.filter((item) => !!item?.properties?.['_freedrop']);

      if (existingFreeItems.length) {
        const updates = existingFreeItems.reduce((acc, item) => {
          acc[item.key] = 0;
          return acc;
        }, {});
  
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        });
        cart = await fetch(`/cart.js?t=${Date.now()}`).then((response) => response.json());
      }

      const freedrop = await fetch("https://st8jv6iz98.execute-api.us-west-2.amazonaws.com/STG2/checkout/freedrop", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...(window?.customer?.id && { customerId: window.customer.id }),
          cart: cart,
        }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => null);
          if (!res.ok) {
            console.warn("freedrop request failed", { status: res.status, data });
            return null;
          }
          return data;
        })
        .catch((e) => {
          console.log(e);
          return null;
        });
      
      
      console.log("freedrop FREEDROP_IN_PROGRESS", freedrop);

      if (freedrop && freedrop.items && freedrop.items.length ) {
        const items = freedrop.items
          .map((item) => {
            if (typeof item === "string") {
              const [id, dropId, quantity] = item.split(":");
              return {
                id: Number(id),
                quantity: Number(quantity) || 1,
                properties: {
                  _freedrop: dropId,
                },
              };
            }
            const variantId = Number(item.variantId ?? item.id);
            const dropId = item.freedropId ?? item.dropId;
            if (!Number.isFinite(variantId) || variantId <= 0 || dropId == null) return null;
            return {
              id: variantId,
              quantity: Number(item.quantity) || 1,
              properties: {
                _freedrop: String(dropId),
              },
            };
          })
          .filter(Boolean);

        if (items.length) {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ items }),
          }).catch(async (e) => {
            console.log(e);
            for (const item of items) {
              await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ items: [item] }),
              });
            }
          });

          localStorage.setItem("FREEDROP", true);
          cart = await fetch(`/cart.js?t=${Date.now()}`).then((response) => response.json());

          console.log("freedrop added to cart");
        }
        
      }
      

      ///////////////////////////////////////////////////////////////////////////////

      // Theme UDF gate (before /checkout). Soft fail: log + measure, then continue.
      // Consultant: stamp/keep parent-line UDFs. Non-consultant: strip all _UDF*.
      // Do not hard-block checkout if stamping/validation is incomplete.
      let udfPreflightIssue = null;
      try {
        const udfPreflight = await prepareJafraCartUdfBeforeCheckoutAsync();
        cart =
          udfPreflight?.cart ||
          (await fetch(`/cart.js?t=${Date.now()}`).then((response) => response.json()));

        if (!udfPreflight?.ok) {
          udfPreflightIssue = {
            status: "incomplete",
            validation: udfPreflight?.validation || [],
            results: (udfPreflight?.results || []).map((result) => ({
              variantId: result.variantId || "",
              changed: Boolean(result.changed),
              reason: result.reason || "",
              fatal: Boolean(result.fatal),
              isBundle: Boolean(result.isBundle),
            })),
            isConsultant: isJafraConsultantCustomer(),
            proceed: true,
          };
          traceJafraTaxThemeEvent("checkout.udf_preflight.soft_fail", udfPreflightIssue);
          console.error(
            "jafra checkout UDF preflight incomplete; allowing Proceed",
            udfPreflightIssue.validation,
            udfPreflightIssue.results,
          );
        }
      } catch (error) {
        udfPreflightIssue = {
          status: "failed",
          error: error?.message || String(error),
          isConsultant: isJafraConsultantCustomer(),
          proceed: true,
        };
        traceJafraTaxThemeEvent("checkout.udf_preflight.failed", udfPreflightIssue);
        console.error("jafra checkout UDF preflight failed; allowing Proceed", error);
        try {
          cart = await fetch(`/cart.js?t=${Date.now()}`).then((response) => response.json());
        } catch (_) {
          // Keep existing cart reference if refresh fails.
        }
      }

      $(document.body).addClass("loading-checkout");
      customCheckout.prop("disabled", true);

      let urlCustomerData = "";

      if (shopAsCustomer) {
        const extensionStorage = localStorage.getItem("extensionStorage");
        if (extensionStorage) localStorage.removeItem("extensionStorage");

        customerId = parseFloat(
          shopAsCustomer.id.replace("gid://shopify/Customer/", "")
        );
        // create checkout link customer data string
        urlCustomerData = `checkout[email]=${
          shopAsCustomer.email
        }&checkout[shipping_address][first_name]=${
          shopAsCustomer.defaultAddress.firstName
        }&checkout[shipping_address][last_name]=${
          shopAsCustomer.defaultAddress.lastName
        }&checkout[shipping_address][address1]=${encodeURIComponent(
          shopAsCustomer.defaultAddress.address1
        )}&checkout[shipping_address][address2]=${encodeURIComponent(
          shopAsCustomer.defaultAddress.address2 ?? ""
        )}&checkout[shipping_address][city]=${encodeURIComponent(
          shopAsCustomer.defaultAddress.city
        )}&checkout[shipping_address][country]=${encodeURIComponent(
          shopAsCustomer.defaultAddress.country
        )}&checkout[shipping_address][province]=${
          shopAsCustomer.defaultAddress.province
        }&checkout[shipping_address][zip]=${
          shopAsCustomer.defaultAddress.zip
        }&checkout[shipping_address][phone]=${
          shopAsCustomer.defaultAddress.phone
        }`;

        if (window.location.pathname !== "/cart") {
          return (window.location.href = "/cart");
        }
      }

      let bundles = [];
      let hasKit = false;
      let isGroupOrder = "false";
      let groupOrderID = "NONE";

      if (cart === undefined) {
        cart = await fetch("/cart.js").then((res) => res.json());
      }

      let totalCommissionable = 0;
      cart.items.forEach((item) => {
        if (item.product_type === "Kit") {
          hasKit = true;
        } else if (item.product_title.includes("Bundle")) {
          bundles.push(item);
        }
        if (item.properties && item.properties["Group Order Customer"]) {
          isGroupOrder = "true";
        }
        if (item.properties && item.properties["_GroupOrderCustomerID"]) {
          groupOrderCustomerID = item.properties["_GroupOrderCustomerID"];
          isGroupOrder = "false";
        }

        if(item.properties && item.properties["_commissionable"] === "Commissionable" || item.properties["_Category"] === "Commissionable" || item.properties["Category"] === "Commissionable" || item.properties["_set_type"] === "Kit Add-On") {
          totalCommissionable +=
            parseFloat(item.original_line_price) /
            100;
        }

      });

      // Handle group order checkout
      if (isGroupOrder === "true") {
        groupOrderID = window.customer.groupOrderID;
        const extensionStorage =
          window.localStorage.getItem("extensionStorage");
        if (extensionStorage) {
          const extensionStorageObj = JSON.parse(extensionStorage);
          const giftCardsKey = Object.keys(extensionStorageObj).find((key) =>
            key.includes("GROUP_ORDER_GIFT_CARD")
          );
          if (giftCardsKey) delete extensionStorageObj[giftCardsKey];
          window.localStorage.setItem(
            "extensionStorage",
            JSON.stringify(extensionStorageObj)
          );
        }

        try {
          await toggleTags(["group-order-in-progress"], []);
        } catch (error) {
          console.error(error);
        }
      } else {
        try {
          await toggleTags([], ["group-order-in-progress"]);
        } catch (error) {
          console.error(error);
        }
      }
      let sessionId = sessionStorage.getItem("CHECKOUT_CUSTOM_SESSION_ID");
      if (!sessionId) {
        sessionId = Date.now();
        sessionStorage.setItem("CHECKOUT_CUSTOM_SESSION_ID", sessionId);
      }

      // enrollment tax logic
      let enrollmentTax = false;


      // update commission_table values
      const commission_table = {};

      if(document.getElementById("_commissionables_original_subtotal") !== null) {
        commission_table.retail_value = {
          product_total: parseFloat(document.getElementById("_commissionables_original_subtotal").innerText.replace("$", "").replace(",", "")),
          commission: parseFloat(document.getElementById("_commissionable_discount").innerText.replace("%", "")),
          subtotal: parseFloat(document.getElementById("_commissionables_total").innerText.replace("$", "").replace(",", ""))
        }
        commission_table.non_retail_value = {
          product_total: parseFloat(document.getElementById("_non_commissionables_original_subtotal").innerText.replace("$", "").replace(",", "")),
          commission: parseFloat(document.getElementById("_non_commissionable_discount").innerText.replace("%", "")),
          subtotal: parseFloat(document.getElementById("_non_commissionables_total").innerText.replace("$", "").replace(",", ""))
        }
      }

      let curSite =
        sessionStorage.getItem(OVERRIDE_PARTNER_STORAGE_KEY) ??
        sessionStorage.getItem(CURRENT_PARTNER_STORAGE_KEY) ??
        null;
      console.log("curSite: " + curSite);

      // if cursite null, check if pws url exists and set as curSite
      if (curSite === null) {
        const url = new URL(window.location.href);
        const pwsUrl = url.searchParams.get("pws");
        if (pwsUrl) {
          curSite = pwsUrl;
        }
      }

      const brandAmbassadorId = await getPartnerIdBySite(curSite);
      console.log("brandAmbassadorId: " + brandAmbassadorId);

      const context = {
        curSite: curSite,
        customerId: window?.customer?.id ?? null,
        customerEmail: window?.customer?.email ?? null,
        groupOrderID: groupOrderID ?? null,
        isPartner: window?.customer?.isPartner ?? false,
        isQualifiedConsultant: window?.customer?.isQualifiedConsultant ?? false,
        sapConsultantSoldToId: window?.customer?.sapConsultantSoldToId ?? null,
        partyId: sessionStorage.getItem("URL_PARTY") ?? null,
        enrollmentTax: enrollmentTax ?? false,
        BrandAmbassadorId: brandAmbassadorId,
        shopAsSelected: shopAsCustomer ? true : false,
        taxExempt: window.customer ? window.customer.taxExempt === 'true' : false,
        nativeTaxExempt: window.customer
          ? window.customer.nativeTaxExempt === true ||
            window.customer.nativeTaxExempt === "true"
          : false,
        //userType: window.customer ? window.customer.userType : null,
         userType: window.customer.userType ?? null,
        totalCommissionable: totalCommissionable,
        commissionTable: commission_table ? commission_table : null,
        udfPreflightStatus: udfPreflightIssue?.status || "ok",
      };

      const checkoutUrl = new URL('/checkout', window.location.origin);
      const attributes = {
        GroupOrder: isGroupOrder,
        sessionID: sessionId,
        context: btoa(JSON.stringify(context))
      };

      if (sessionStorage.getItem("LOGIN_SOURCE")) {
        attributes["Order Impersonated By"] = sessionStorage.getItem("LOGIN_SOURCE");
      }

      // Measurable note when UDF preflight could not fully complete (soft fail).
      if (udfPreflightIssue) {
        attributes["_jafra_udf_preflight"] = udfPreflightIssue.status;
        const reasons = (udfPreflightIssue.validation || [])
          .map((item) => item?.reason)
          .filter(Boolean)
          .slice(0, 5);
        if (reasons.length) {
          attributes["_jafra_udf_preflight_reason"] = reasons.join("|").slice(0, 250);
        } else if (udfPreflightIssue.error) {
          attributes["_jafra_udf_preflight_reason"] = String(udfPreflightIssue.error).slice(0, 250);
        }
      }

      // Add all attributes to URL params
      Object.entries(attributes).forEach(([key, value]) => {
        checkoutUrl.searchParams.append(`attributes[${key}]`, value);
      });

      // Add skip_shop_pay parameter for consultant checkouts to disable Shop Pay auto-redirect
      const isConsultantCheckout = context.isPartner || curSite;
      if (isConsultantCheckout) {
        checkoutUrl.searchParams.append('skip_shop_pay', 'true');
      }

      if (window?.customer?.defaultAddress) {
        const address = window.customer.defaultAddress;
        checkoutUrl.searchParams.append("checkout[shipping_address][first_name]", address.first_name);
        checkoutUrl.searchParams.append("checkout[shipping_address][last_name]", address.last_name);
        checkoutUrl.searchParams.append("checkout[shipping_address][address1]", address.address1);
        checkoutUrl.searchParams.append("checkout[shipping_address][city]", address.city);
        checkoutUrl.searchParams.append("checkout[shipping_address][country]", address.country);
        checkoutUrl.searchParams.append("checkout[shipping_address][province]", address.province);
        checkoutUrl.searchParams.append("checkout[shipping_address][zip]", address.zip);
      }

      // clear checkout cache
      localStorage.removeItem("__ui");

        // logging for debugging (+ dedicated UDF preflight soft-fail payload when needed)
        await fetch(
          "https://st8jv6iz98.execute-api.us-west-2.amazonaws.com/STG2/frontend/error-log",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              cartToken: cart.token,
              cart: cart,
              context: context,
              url: checkoutUrl.toString(),
              cartUrl: window.location.href,
              ...(udfPreflightIssue
                ? {
                    errorType: "udf_preflight_soft_fail",
                    level: "error",
                    message: "UDF preflight incomplete; checkout allowed to proceed",
                    udfPreflight: udfPreflightIssue,
                  }
                : {}),
            }),
          }
        )
          .then((res) => res.json())
          .then((data) => {
            return data;
          })
          .catch((e) => console.log(e));

      window.location.href = checkoutUrl.toString();
    });

    console.log("========= congigureCustomCheckout end =========");
  }

  function formatMoney(cents) {
    const formatString = window.theme?.moneyFormat || "${{amount}}";
    
    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) { return 0; }

      number = (number/100.0).toFixed(precision);

      var parts = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          cents = parts[1] ? (decimal + parts[1]) : '';

      return dollars + cents;
    }

    return formatString.replace(/\{\{\s*(\w+)\s*\}\}/, function(_, amount) {
      return formatWithDelimiters(cents, 2);
    });
  }

  function updateVIPTracker(cart) {
    const vipTracker = document.querySelector('[data-vip-tracker]');
    if (!vipTracker) return;

    // Calculate cart retail value (commissionable items only)
    let cartRetailValue = 0;
    if (cart && cart.items) {
      cart.items.forEach(item => {
        // Exclude Non Commissionable items
        const isNonCommissionable = 
          item.product_type === 'Non Commissionable' ||
          item.properties?._commissionable === 'Non-Commissionable' ||
          item.properties?._Category === 'Non-Commissionable';
        
        if (!isNonCommissionable) {
          cartRetailValue += item.original_line_price;
        }
      });
    }

    // Get MTD from data attribute or window
    let mtdRetailSales = 0;
    if (window.customer && window.customer.metafields) {
      mtdRetailSales = window.customer.metafields.mtd_retail_sales || 0;
    }

    // Calculate total
    const totalRetail = mtdRetailSales + cartRetailValue;
    const vipMinimum = 40000; // $400.00 in cents
    const remaining = vipMinimum - totalRetail;
    const hasReachedGoal = totalRetail >= vipMinimum;

    // Update details (MTD, Cart, Total)
    const mtdElement = vipTracker.querySelector('[data-vip-mtd]');
    if (mtdElement) {
      mtdElement.textContent = formatMoney(mtdRetailSales);
    }

    const cartValueElement = vipTracker.querySelector('[data-vip-cart]');
    if (cartValueElement) {
      cartValueElement.textContent = formatMoney(cartRetailValue);
    }

    const totalElement = vipTracker.querySelector('[data-vip-total]');
    if (totalElement) {
      totalElement.textContent = formatMoney(totalRetail);
    }

    // Update banner message
    const bannerElement = vipTracker.querySelector('[data-vip-message]');
    if (bannerElement) {
      const bannerWrapper = bannerElement.querySelector('.cart__vip-tracker__banner-wrapper');
      if (!bannerWrapper) return;

      if (hasReachedGoal) {
        bannerElement.classList.remove('cart__vip-tracker__banner--success');
        bannerElement.classList.add('cart__vip-tracker__banner--success');
        const goalAchievedMessage = window.theme.wrapVIPAmount(
          window.theme.strings.vipTrackerGoalAchieved.replace('[mtd_retail]', formatMoney(totalRetail))
        );
        bannerWrapper.innerHTML = `
          <div class="cart__vip-tracker__banner-content">
            <svg class="cart__vip-tracker__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" fill="#4CAF50" stroke="#4CAF50" stroke-width="2"/>
              <path d="M6 10L9 13L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="cart__vip-tracker__message-text">${goalAchievedMessage}</span>
          </div>
          <div class="cart__vip-tracker__banner-disclaimer">${window.theme.strings.vipTrackerGoalAchievedDisclaimer}</div>
        `;
      } else {
        bannerElement.classList.remove('cart__vip-tracker__banner--success');
        const belowMinMessage = window.theme.wrapVIPAmount(
          window.theme.strings.vipTrackerBelowMinimum.replace('[amount]', formatMoney(remaining))
        );
        bannerWrapper.innerHTML = `
          <div class="cart__vip-tracker__banner-content">
            <svg class="cart__vip-tracker__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" fill="#FF9800" stroke="#FF9800" stroke-width="2"/>
              <path d="M10 6V11" stroke="white" stroke-width="2" stroke-linecap="round"/>
              <circle cx="10" cy="14" r="0.5" fill="white" stroke="white" stroke-width="1"/>
            </svg>
            <span class="cart__vip-tracker__message-text">${belowMinMessage}</span>
          </div>
          <div class="cart__vip-tracker__banner-disclaimer">${window.theme.strings.vipTrackerBelowMinimumDisclaimer}</div>
        `;
      }
    }

    console.log('[VIP Tracker] Updated:', {
      cartRetailValue,
      mtdRetailSales,
      totalRetail,
      hasReachedGoal
    });
  }

  // Initialize VIP tracker banner message styling on page load
  function initVIPTrackerBannerMessage() {
    const bannerMessage = document.querySelector('[data-vip-banner-message]');
    if (bannerMessage && window.theme.wrapVIPAmount) {
      const currentHTML = bannerMessage.innerHTML;
      bannerMessage.innerHTML = window.theme.wrapVIPAmount(currentHTML);
    }
  }

  // Initialize VIP tracker toggle
  function initVIPTrackerToggle() {
    const toggleBtn = document.querySelector('[data-vip-toggle]');
    const details = document.querySelector('[data-vip-details]');
    
    if (!toggleBtn || !details) return;

    toggleBtn.addEventListener('click', () => {
      const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        details.style.display = 'none';
        toggleBtn.setAttribute('aria-expanded', 'false');
      } else {
        details.style.display = 'block';
        toggleBtn.setAttribute('aria-expanded', 'true');
      }
    });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initVIPTrackerBannerMessage();
      initVIPTrackerToggle();
    });
  } else {
    initVIPTrackerBannerMessage();
    initVIPTrackerToggle();
  }

  async function toggleTags(tagsToAdd, tagsToRemove) {
    let customerId = ShopifyAnalytics?.meta?.page?.customerId;
    if (!customerId) return;
    const url = `https://st8jv6iz98.execute-api.us-west-2.amazonaws.com/STG2/partners/${customerId}/tags`;

    const body = {
      add: tagsToAdd,
      remove: tagsToRemove,
    };

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        if (!res.ok) {
          return res.text().then((text) => {
            throw new Error(text);
          });
        } else {
          return res.json();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  async function swapBundles(bundles, targetBundleTag) {
    return; // disable bundle swap
    const fetchVariantPromises = bundles.map(async (bundle) => {
      try {
        const response = await fetch(
          `https://st8jv6iz98.execute-api.us-west-2.amazonaws.com/STG2/bundles/${
            bundle.product_id
          }/variants?tag=${encodeURIComponent(targetBundleTag)}`
        );
        const variants = await response.json();
        if (variants.length && bundle.id !== variants[0].id) {
          return {
            ori: bundle.id,
            new: variants[0].id,
            quantity: bundle.quantity,
            properties: bundle.properties,
          };
        }
      } catch (error) {
        console.error(
          `Error fetching variants for bundle with ID ${bundle.id}:`,
          error
        );
      }
      return null;
    });

    const variantResults = await Promise.allSettled(fetchVariantPromises);
    const changeSet = variantResults
      .filter((result) => result.status === "fulfilled" && result.value)
      .map((result) => result.value);

    if (changeSet.length) {
      const updates = {};
      changeSet.forEach((item) => {
        updates[item.ori] = 0;
      });

      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Bundle-Swap": true },
        body: JSON.stringify({ updates }),
        redirect: "manual",
      });

      // new Promise(r => setTimeout(r, 200));

      const addItems = changeSet.map((item) => ({
        id: item.new,
        quantity: item.quantity,
        properties: item.properties,
      }));
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Bundle-Swap": true },
        body: JSON.stringify({ items: addItems.reverse() }),
        redirect: "manual",
      });

      // new Promise(r => setTimeout(r, 200));
    }
  }

  async function getPartnerIdBySite(storedCustomerKey) {
    let shopAsPartner = await fetch(
      `https://st8jv6iz98.execute-api.us-west-2.amazonaws.com/STG2/partners?website=${storedCustomerKey}`,
      { method: "GET" }
    )
      .then((response) => response.json())
      .then((response) => {
        return response;
      })
      .catch((err) => console.error(err));

    if (shopAsPartner !== null) {
      return shopAsPartner.legacyResourceId;
    }
    return null;
  }

  async function checkoutChecks(cart, customerId) {
    if (customerId === undefined) {
      // check if shop_as customer and pull customerId from sessionStorage
      const CURRENT_SHOP_AS_CUSTOMER_STORAGE_KEY =
        "CURRENT_SHOP_AS_CUSTOMER_STORAGE_KEY";
      const storedCustomerKey = sessionStorage.getItem(
        CURRENT_SHOP_AS_CUSTOMER_STORAGE_KEY
      );
      if (!storedCustomerKey !== undefined) {
        customerId = await getPartnerIdBySite(storedCustomerKey);
      }
    }
    // console.log("customerId: " + customerId)

    if (customerId !== undefined && customerId !== null) {
      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId: customerId,
          cart: cart,
        }),
      };
      console.log(options);

      var checkoutCheckstartTime = performance.now();
      let { qualifiedCheck, tagCheck } = await fetch(
        "https://st8jv6iz98.execute-api.us-west-2.amazonaws.com/STG2/partners/checkout-checks",
        options
      )
        .then((response) => {
          if (response.status !== 200) {
            return null;
          }
          return response.json();
        })
        .then((response) => {
          if (response === null) {
            return { qualifiedCheck: null, tagCheck: null };
          }
          return response;
        })
        .catch((err) => console.error(err));
      var checkoutCheckendTime = performance.now();

      console.log(
        `==== checkoutChecks took ${
          checkoutCheckendTime - checkoutCheckstartTime
        } milliseconds =====`
      );

      console.log("qualifiedCheck: " + JSON.stringify(qualifiedCheck));
      console.log("tagCheck: " + JSON.stringify(tagCheck));

      if (qualifiedCheck === null) {
        localStorage.setItem("qualifiedCheck", false);
        return false;
      }

      try {
        let cachedCheck =
          localStorage.getItem("qualifiedCheck") === "undefined"
            ? undefined
            : JSON.parse(localStorage.getItem("qualifiedCheck"));
        if (
          cachedCheck === undefined ||
          cachedCheck !== qualifiedCheck.qualified
        ) {
          localStorage.setItem("qualifiedCheck", qualifiedCheck.qualified);
        }
      } catch (error) {
        console.log(error);
      }

      return { qualifiedCheck, tagCheck };
    }
    return false;
  }

  function openCommissionWarningModal(modal, discountAmount, cartTotal) {
    // calculate progress
    let progressPercent = (cartTotal / discountAmount) * 100;
    let progressDifference = discountAmount - cartTotal;

    let modalTranslations = JSON.parse(commissionModalTranslations);

    let progressMessage = document.getElementById(
      "commission-adjt-warning__progress-message"
    );
    progressMessage.innerHTML = modalTranslations.commission_progress_message;
    progressMessage.innerHTML = progressMessage.innerHTML.replace(
      "{{progress_difference}}",
      progressDifference.toFixed(2)
    );

    let progressBar = document.getElementById(
      "commission-adjt-warning__progress-bar"
    );
    progressBar.setAttribute("value", Math.round(progressPercent));

    // set adjustment amount in message
    let warningMessage = document.getElementById(
      "commission-adjt-warning__message"
    );
    warningMessage.innerHTML = modalTranslations.commission_warning_body;
    warningMessage.innerHTML = warningMessage.innerHTML.replace(
      "{{adjustment_amount}}",
      discountAmount.toFixed(2)
    );

    const modalClose = modal.querySelectorAll("[data-micromodal-close]");
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    modalClose.forEach((closeElement) =>
      closeElement.addEventListener("click", function () {
        modal.classList.remove("is-open");
        modal.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        $("#cart-page-checkout").prop("disabled", false);
      })
    );
  }

  window.onload = function () {
    const loginLogoutButtons = document.querySelectorAll(
      "#drawer-account [href*='logout'], #CustomerLoginFormDrawer button, #customer_login button"
    );

    loginLogoutButtons.length > 0 &&
      loginLogoutButtons.forEach((button) => {
        button.addEventListener("click", function () {
          localStorage.setItem("modalShown", "false");
          localStorage.setItem("PASSWORD_RESET_MODAL_SHOWN", false);
        });
      });

    function openModal(modal) {
      const showFormButton = modal.querySelector("[data-show-form-button]");
      const modalContent = modal.querySelector(".ssn-reminder");
      const modalSsnForm = modal.querySelector(".ssn-reminder-form");
      const modalClose = modal.querySelectorAll("[data-micromodal-close]");
      modal.classList.add("is-open");
      modal.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";

      showFormButton.addEventListener("click", function () {
        modalContent.classList.add("hide");
        modalSsnForm.classList.remove("hide");
      });

      modalClose.forEach((closeElement) =>
        closeElement.addEventListener("click", function () {
          modal.classList.remove("is-open");
          modal.setAttribute("aria-hidden", "true");
          document.body.style.overflow = "";
        })
      );
    }

    if (localStorage.getItem("modalShown") !== "true") {
      const modal = document.getElementById("ssn-reminder");
      if (!modal) return;
      openModal(modal);
      localStorage.setItem("modalShown", "true");
    }
  };

  const initCart = await fetch("/cart.js").then((res) => res.json());
  if (window.FGSECOMAPP) {
    window.FGSECOMAPP.cart = initCart
  }

  // Password requirements logic
  // Existing DOM Elements
  const passwordField = document.getElementById("CreatePassword");
  const editPasswordField = document.getElementById("profile-password-input");
  const confirmPasswordField = document.getElementById(
    "profile-confirm-password-input"
  );
  const passwordResetField = document.getElementById("ResetPassword");
  const passwordResetConfirmField = document.getElementById(
    "PasswordConfirmation"
  );

  // New DOM Elements
  const customerPasswordField = document.getElementById("CustomerPassword");
  const customerPasswordConfirmField = document.getElementById(
    "CustomerPasswordConfirmation"
  );

  const requirements = document.querySelectorAll(".password-requirements li");

  const submitButton = document.querySelector("[data-password-submit]");
  const resetSubmitButton = document.querySelector(
    "[data-password-reset-submit]"
  );
  const activateSubmitButton = document.querySelector("[data-activate-submit]");

  const togglePasswordButton = document.getElementById("togglePassword");
  const togglePasswordConfirmButton = document.getElementById(
    "toggleConfirmPassword"
  );
  const togglePasswordResetButton = document.getElementById(
    "toggleResetPassword"
  );
  const togglePasswordResetConfirmButton = document.getElementById(
    "toggleConfirmResetPassword"
  );

  // New Toggle Buttons
  const toggleCustomerPasswordButton = document.getElementById(
    "toggleCustomerPassword"
  );
  const toggleCustomerPasswordConfirmButton = document.getElementById(
    "toggleCustomerPasswordConfirm"
  );

  const toggleActivatePasswordButton = document.getElementById(
    "toggleActivatePassword"
  );
  const toggleConfirmActivatePasswordButton = document.getElementById(
    "toggleConfirmActivatePassword"
  );

  let passwordVisible = false;

  // Function to toggle password visibility
  function toggleVisibility(button, field, iconId) {
    passwordVisible = !passwordVisible;
    const type = passwordVisible ? "text" : "password";
    if (field) field.setAttribute("type", type);
    document.getElementById(iconId).src = passwordVisible
      ? hidePasswordIconUrl
      : showPasswordIconUrl;
  }

  if (togglePasswordButton) {
    togglePasswordButton.addEventListener("click", function () {
      toggleVisibility(togglePasswordButton, passwordField, "toggleIcon");
      toggleVisibility(togglePasswordButton, editPasswordField, "toggleIcon");
    });
  }

  if (togglePasswordConfirmButton) {
    togglePasswordConfirmButton.addEventListener("click", function () {
      toggleVisibility(
        togglePasswordConfirmButton,
        confirmPasswordField,
        "toggleConfirmationIcon"
      );
    });
  }

  if (togglePasswordResetButton) {
    togglePasswordResetButton.addEventListener("click", function () {
      toggleVisibility(
        togglePasswordResetButton,
        passwordResetField,
        "toggleResetIcon"
      );
    });
  }

  if (togglePasswordResetConfirmButton) {
    togglePasswordResetConfirmButton.addEventListener("click", function () {
      toggleVisibility(
        togglePasswordResetConfirmButton,
        passwordResetConfirmField,
        "toggleConfirmationResetIcon"
      );
    });
  }

  // New Toggle Visibility Event Listeners
  if (toggleCustomerPasswordButton) {
    toggleCustomerPasswordButton.addEventListener("click", function () {
      toggleVisibility(
        toggleCustomerPasswordButton,
        customerPasswordField,
        "toggleCustomerPasswordIcon"
      );
    });
  }

  if (toggleCustomerPasswordConfirmButton) {
    toggleCustomerPasswordConfirmButton.addEventListener("click", function () {
      toggleVisibility(
        toggleCustomerPasswordConfirmButton,
        customerPasswordConfirmField,
        "toggleCustomerPasswordConfirmIcon"
      );
    });
  }

  if (toggleActivatePasswordButton) {
    toggleActivatePasswordButton.addEventListener("click", function () {
      toggleVisibility(
        toggleActivatePasswordButton,
        customerPasswordField,
        "toggleActivatePasswordIcon"
      );
    });
  }

  if (toggleConfirmActivatePasswordButton) {
    toggleConfirmActivatePasswordButton.addEventListener("click", function () {
      toggleVisibility(
        toggleConfirmActivatePasswordButton,
        customerPasswordConfirmField,
        "toggleActivationConfirmationIcon"
      );
    });
  }

  // Function to validate password
  function validatePassword(password) {
    const lengthRequirement = password.length >= 8 && password.length <= 20;
    const uppercaseRequirement = /[A-Z]/.test(password);
    const lowercaseRequirement = /[a-z]/.test(password);
    const numberRequirement = /\d/.test(password);
    const specialCharRequirement = /[!@#$%^&*]/.test(password);

    const requirementsMet =
      lengthRequirement &&
      uppercaseRequirement &&
      lowercaseRequirement &&
      numberRequirement &&
      specialCharRequirement;

    updateRequirementStatus(lengthRequirement, requirements[0]);
    updateRequirementStatus(uppercaseRequirement, requirements[1]);
    updateRequirementStatus(lowercaseRequirement, requirements[2]);
    updateRequirementStatus(numberRequirement, requirements[3]);
    updateRequirementStatus(specialCharRequirement, requirements[4]);

    return requirementsMet;
  }

  // Function to update requirement status
  function updateRequirementStatus(isMet, requirementElement) {
    if (isMet) {
      requirementElement.classList.add("met");
    } else {
      requirementElement.classList.remove("met");
    }
  }

  // Function to handle input events
  function handleInputEvent(
    event,
    passwordField,
    confirmPasswordField,
    submitButton
  ) {
    const password = passwordField.value;
    const confirmPassword = confirmPasswordField.value;
    const requirementsMet = validatePassword(password);

    const warningMessage = document.getElementById("password-warning");

    if (confirmPassword === password && requirementsMet) {
      submitButton.disabled = false;
      warningMessage.style.display = "none";
    } else {
      submitButton.disabled = true;
      if (confirmPassword !== password) {
        warningMessage.style.display = "block";
      } else {
        warningMessage.style.display = "none";
      }
    }
  }

  // Add event listeners to password fields
  if (editPasswordField && confirmPasswordField) {
    editPasswordField.addEventListener("input", function (event) {
      handleInputEvent(
        event,
        editPasswordField,
        confirmPasswordField,
        submitButton
      );
    });

    confirmPasswordField.addEventListener("input", function (event) {
      handleInputEvent(
        event,
        editPasswordField,
        confirmPasswordField,
        submitButton
      );
    });

    confirmPasswordField.addEventListener("blur", function () {
      const confirmPassword = confirmPasswordField.value;
      const password = editPasswordField.value;

      if (confirmPassword !== password) {
        submitButton.disabled = true;
        document.getElementById("password-warning").style.display = "block";
      }
    });
  }

  if (passwordResetField && passwordResetConfirmField) {
    passwordResetField.addEventListener("input", function (event) {
      handleInputEvent(
        event,
        passwordResetField,
        passwordResetConfirmField,
        resetSubmitButton
      );
    });

    passwordResetConfirmField.addEventListener("input", function (event) {
      handleInputEvent(
        event,
        passwordResetField,
        passwordResetConfirmField,
        resetSubmitButton
      );
    });

    passwordResetConfirmField.addEventListener("blur", function () {
      const confirmPassword = passwordResetConfirmField.value;
      const password = passwordResetField.value;

      if (confirmPassword !== password) {
        resetSubmitButton.disabled = true;
        document.getElementById("password-warning").style.display = "block";
      }
    });
  }

  // New Event Listeners for Customer Password Fields
  if (customerPasswordField && customerPasswordConfirmField) {
    customerPasswordField.addEventListener("input", function (event) {
      handleInputEvent(
        event,
        customerPasswordField,
        customerPasswordConfirmField,
        activateSubmitButton
      );
    });

    customerPasswordConfirmField.addEventListener("input", function (event) {
      handleInputEvent(
        event,
        customerPasswordField,
        customerPasswordConfirmField,
        activateSubmitButton
      );
    });

    customerPasswordConfirmField.addEventListener("blur", function () {
      const confirmPassword = customerPasswordConfirmField.value;
      const password = customerPasswordField.value;

      if (confirmPassword !== password) {
        activateSubmitButton.disabled = true;
        document.getElementById("password-warning").style.display = "block";
      }
    });
  }

  // Disable submit buttons by default
  if (submitButton) submitButton.disabled = true;
  if (resetSubmitButton) resetSubmitButton.disabled = true;
  if (activateSubmitButton) activateSubmitButton.disabled = true;
})();

(() => {
  const MY_PROGRESS_SECTION = "retail-leader-dashboard";
  const ESTIMATOR_SECTION = "my-reports-team-sales-commission-estimator";
  const INTL_TRIP_SECTION = "my-reports-international-trip";
  const WIDGET_BASE_URL = "https://duj7fbv99586u.cloudfront.net";

  const locale = () => (document.documentElement.lang || window.theme?.locale || "en").toLowerCase();
  const isSpanish = () => locale().indexOf("es") === 0;
  const myProgressLabel = () => (isSpanish() ? "Mi Progreso" : "My Progress");
  const estimatorLabel = () =>
    isSpanish() ? "Estimador de Comisión de Ventas del Equipo" : "Team Sales Commission Estimator";
  const intlTripLabel = () => (isSpanish() ? "Viaje Internacional 2027" : "International Trip 2027");

  const chartIconSvg = () =>
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.875 12.375H3.125V2.625C3.125 2.55625 3.06875 2.5 3 2.5H2.125C2.05625 2.5 2 2.55625 2 2.625V13.375C2 13.4438 2.05625 13.5 2.125 13.5H13.875C13.9438 13.5 14 13.4438 14 13.375V12.5C14 12.4312 13.9438 12.375 13.875 12.375ZM4.25 11.375H12.625C12.6938 11.375 12.75 11.3188 12.75 11.25V4.4375C12.75 4.325 12.6141 4.27031 12.5359 4.34844L9.25 7.63438L7.29063 5.69688C7.26713 5.67361 7.23541 5.66056 7.20234 5.66056C7.16928 5.66056 7.13756 5.67361 7.11406 5.69688L4.16094 8.65938C4.14947 8.67087 4.14039 8.68451 4.13422 8.69953C4.12805 8.71455 4.12492 8.73064 4.125 8.74687V11.25C4.125 11.3188 4.18125 11.375 4.25 11.375Z" fill="#575757"/></svg>';

  const arrowIconSvg = () =>
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon--wide icon-arrow-right-long" viewBox="0 0 21 10"><path d="M14.982 1.01l.696-.717 3.803 3.691.011-.01.338.348.359.348-.012.01.012.011-.354.343-.343.353-.011-.011-3.803 3.692-.696-.718 3.266-3.17H.5v-1h17.748z"></path></svg>';

  const consultantPayload = (dashboard) => {
    const name = document.querySelector("[data-panel-name]")?.textContent.trim() || "";
    const [firstName = "", ...lastNameParts] = name.split(/\s+/).filter(Boolean);
    return {
      consultantId: dashboard.dataset.consultantId || "",
      customerId: String(window?.customer?.id || dashboard.dataset.customerId || ""),
      firstName,
      lastName: lastNameParts.join(" "),
      email: document.querySelector(".dashboard-customer__content-text .text--light")?.textContent.trim() || "",
      isPartner: true,
      language: isSpanish() ? "es" : "en",
    };
  };

  const encodedConsultantInfo = (dashboard) => {
    try {
      return btoa(encodeURIComponent(JSON.stringify(consultantPayload(dashboard))));
    } catch (error) {
      console.warn("Unable to encode consultant info for My Progress", error);
      return "";
    }
  };

  const normalizeAccountDashboard = () => {
    const dashboard = document.querySelector('[data-section-type="customer-dashboard"]');
    if (!dashboard) return;

    const accountNav = dashboard.querySelector(".dashboard-nav");
    const reportsNav = [...dashboard.querySelectorAll(".dashboard-nav")].find((nav) =>
      nav.querySelector(".dashboard-nav__title")?.textContent.trim().toLowerCase().includes("report")
    );
    const sectionsContainer = dashboard.querySelector(".dashboard__sections");
    const isPartner = dashboard.dataset.isPartner === "true";

    if (!isPartner) {
      dashboard.querySelectorAll(`[data-nav-for="${MY_PROGRESS_SECTION}"]`).forEach((nav) => nav.remove());
      dashboard.querySelector(`[data-dashboard-section="${MY_PROGRESS_SECTION}"]`)?.remove();
      return;
    }

    if (accountNav && !dashboard.querySelector(`[data-nav-for="${MY_PROGRESS_SECTION}"]`)) {
      const myProgressNav = document.createElement("div");
      myProgressNav.className = "dashboard-nav__item";
      myProgressNav.dataset.navFor = MY_PROGRESS_SECTION;
      myProgressNav.innerHTML = `${chartIconSvg()} ${myProgressLabel()}`;

      const personalizeNav = accountNav.querySelector('[data-nav-for="personalize"]');
      accountNav.insertBefore(myProgressNav, personalizeNav || null);
    }

    dashboard.querySelectorAll(`[data-nav-for="${MY_PROGRESS_SECTION}"]`).forEach((nav) => {
      nav.innerHTML = `${chartIconSvg()} ${myProgressLabel()}`;
    });

    const legacySection = dashboard.querySelector('[data-dashboard-section="my-performance"]');
    const estimatorSection = dashboard.querySelector(`[data-dashboard-section="${ESTIMATOR_SECTION}"]`);
    if (legacySection && !estimatorSection) {
      legacySection.dataset.dashboardSection = ESTIMATOR_SECTION;
    } else if (legacySection && estimatorSection) {
      legacySection.remove();
    }

    const existingEstimatorNav = dashboard.querySelector(`[data-nav-for="${ESTIMATOR_SECTION}"]`);
    dashboard.querySelectorAll('[data-nav-for="my-performance"]').forEach((legacyNav) => {
      if (existingEstimatorNav && existingEstimatorNav !== legacyNav) {
        legacyNav.remove();
        return;
      }

      legacyNav.dataset.navFor = ESTIMATOR_SECTION;
      legacyNav.innerHTML = `${arrowIconSvg()} ${estimatorLabel()}`;
      if (reportsNav && legacyNav.parentElement !== reportsNav) {
        reportsNav.insertBefore(legacyNav, reportsNav.querySelector('[data-nav-for="points-ledger"]'));
      }
    });

    dashboard.querySelectorAll(`[data-nav-for="${ESTIMATOR_SECTION}"]`).forEach((nav) => {
      nav.innerHTML = `${arrowIconSvg()} ${estimatorLabel()}`;
    });

    if (reportsNav && !dashboard.querySelector(`[data-nav-for="${INTL_TRIP_SECTION}"]`)) {
      const intlNav = document.createElement("div");
      intlNav.className = "dashboard-nav__item";
      intlNav.dataset.navFor = INTL_TRIP_SECTION;
      intlNav.innerHTML = `${arrowIconSvg()} ${intlTripLabel()}`;
      const incentiveNav =
        reportsNav.querySelector('[data-nav-for="my-reports-incentive-trip"]') ||
        reportsNav.querySelector('[data-nav-for="my-reports-summer-trip"]');
      if (incentiveNav) {
        incentiveNav.after(intlNav);
      } else {
        reportsNav.insertBefore(intlNav, reportsNav.querySelector('[data-nav-for="points-ledger"]'));
      }
    }

    if (sectionsContainer && !dashboard.querySelector(`[data-dashboard-section="${INTL_TRIP_SECTION}"]`)) {
      const encodedData = encodedConsultantInfo(dashboard);
      const section = document.createElement("div");
      section.className = "dashboard__section";
      section.dataset.dashboardSection = INTL_TRIP_SECTION;
      section.style.padding = "0";
      section.innerHTML = `
        <div class="dashboard__block dashboard__block--withspace">
          <button class="dashboard__back" type="button" data-dashboard-back>
            <span class="visually-hidden">Back</span>
          </button>
        </div>
        <div class="page-width" style="height:100vh;overflow:hidden;">
          <iframe
            id="my-reports-international_trip"
            data-src="${WIDGET_BASE_URL}/my-reports?type=international_trip&t=${Date.now()}&consultantInfo=${encodedData}"
            style="width: 100%; height: 100vh; border: 0;"
            title="${intlTripLabel()}"
            allow="clipboard-read; clipboard-write"></iframe>
        </div>
      `;
      sectionsContainer.appendChild(section);
    }

    if (sectionsContainer && !dashboard.querySelector(`[data-dashboard-section="${MY_PROGRESS_SECTION}"]`)) {
      const encodedData = encodedConsultantInfo(dashboard);
      const section = document.createElement("div");
      section.className = "dashboard__section";
      section.dataset.dashboardSection = MY_PROGRESS_SECTION;
      section.style.padding = "0";
      section.innerHTML = `
        <div class="dashboard__block dashboard__block--withspace">
          <button class="dashboard__back" type="button" data-dashboard-back>
            <span class="visually-hidden">Back</span>
          </button>
        </div>
        <div class="page-width" style="height:100vh;overflow:hidden;">
          <iframe
            id="my-reports-retail_leader_dashboard_new"
            data-src="${WIDGET_BASE_URL}/my-reports?type=retail_leader_dashboard_new&t=${Date.now()}&consultantInfo=${encodedData}"
            style="width: 100%; height: 100vh; border: 0;"
            title="${myProgressLabel()}"
            allow="clipboard-read; clipboard-write"></iframe>
        </div>
      `;
      sectionsContainer.insertBefore(section, dashboard.querySelector(`[data-dashboard-section="${ESTIMATOR_SECTION}"]`));
    }

    if (window.location.hash === "#my-performance") {
      window.history.replaceState(null, "", `#${ESTIMATOR_SECTION}`);
    }

    if (window.location.hash === `#${INTL_TRIP_SECTION}`) {
      dashboard.querySelector(`[data-nav-for="${INTL_TRIP_SECTION}"]`)?.click();
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", normalizeAccountDashboard);
  } else {
    normalizeAccountDashboard();
  }
})();
